/**
 * Client-portal layout (SPEC E4 - client-portal, R3).
 *
 * A clean, branded shell for CLIENT-role users - NO operator sidebar, NO
 * TenantSwitcher, NO admin links. Header shows the tenant's branding (logo +
 * display name, E6 tenant_branding inlined on /api/me), the signed-in user's
 * email, and a sign-out button. The primary brand color tints the header accent.
 *
 * Branding degradation (E4-b): when the tenant has no tenant_branding doc the
 * backend resolves a stock default (display_name == tenant_id, stock colors, no
 * logo), so this renders a clean generic header - never broken. The greenmap
 * retirement becomes an upgrade only once dvw4ea's branding is authored.
 */

import { Outlet } from 'react-router-dom'
import { useAuth } from '@/lib/auth/AuthProvider'
import { Button } from '@/components/ui/button'
import {
  useBrandingTheme,
  BRAND_PRIMARY_VAR,
  BRAND_ACCENT_VAR,
} from '@/lib/app-template'

const DEFAULT_PRIMARY = '#2563eb'
const DEFAULT_ACCENT = '#7c3aed'

export function PortalLayout() {
  const { user, signOut } = useAuth()

  const branding = user?.branding
  // Header title: tenant branding display_name → portal client_label → tenant_id.
  const title =
    branding?.display_name || user?.client_label || user?.tenant_id || 'Portal'
  const primary = branding?.primary_color || DEFAULT_PRIMARY
  const accent = branding?.accent_color || DEFAULT_ACCENT
  const logoUrl = branding?.logo_url

  // SPEC PX-11 R2 (AC8) - E6 branding light-up: drives document.title from
  // display_name + swaps the favicon from favicon_url, and returns the inline
  // style that binds the per-tenant --brand-primary / --brand-accent overrides
  // (the FE-1 soft-gate target; absent branding → stock token defaults).
  const themeStyle = useBrandingTheme(branding)

  return (
    <div
      className="min-h-screen bg-background"
      style={themeStyle}
      data-testid="portal-root"
      data-brand-primary={branding?.primary_color || undefined}
      data-brand-accent={branding?.accent_color || undefined}
    >
      <header
        className="border-b bg-card"
        style={{
          borderTopColor: `var(${BRAND_PRIMARY_VAR}, ${primary})`,
          borderTopWidth: 3,
          // expose accent on the header so an accent-tinted child can read it.
          [BRAND_ACCENT_VAR as string]: accent,
        } as React.CSSProperties}
      >
        <div className="mx-auto flex max-w-6xl items-center justify-between px-6 py-4">
          <div className="flex items-center gap-3">
            {logoUrl ? (
              <img
                src={logoUrl}
                alt={`${title} logo`}
                className="h-9 w-auto object-contain"
              />
            ) : (
              <div
                className="flex h-9 w-9 items-center justify-center rounded-md text-sm font-semibold text-white"
                style={{ backgroundColor: `var(${BRAND_PRIMARY_VAR}, ${primary})` }}
                aria-hidden
              >
                {title.slice(0, 2).toUpperCase()}
              </div>
            )}
            <h1 className="text-lg font-heading font-semibold tracking-tight text-foreground" data-testid="portal-title">
              {title}
            </h1>
          </div>
          <div className="flex items-center gap-4">
            <span className="text-sm text-muted-foreground" data-testid="portal-user-email">
              {user?.email}
            </span>
            <Button variant="outline" size="sm" onClick={() => signOut()}>
              Sign out
            </Button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-6xl px-6 py-6">
        <Outlet />
      </main>
    </div>
  )
}
